import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Info, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';

export default function HeroBanner({ items = [] }) {
  const [current, setCurrent] = useState(0);
  const [muted, setMuted] = useState(true);
  const [phase, setPhase] = useState('thumbnail'); // thumbnail -> trailer -> ending
  const videoRef = useRef(null);
  const phaseTimer = useRef(null);

  const goNext = useCallback(() => {
    setCurrent(prev => (prev + 1) % items.length);
  }, [items.length]);

  // On slide change: show thumbnail 2s, then trailer
  useEffect(() => {
    setPhase('thumbnail');
    clearTimeout(phaseTimer.current);
    phaseTimer.current = setTimeout(() => setPhase('trailer'), 2000);
    return () => clearTimeout(phaseTimer.current);
  }, [current]);

  // When trailer ends → show thumbnail 2s → next slide
  const handleTrailerEnded = useCallback(() => {
    if (items.length <= 1) return;
    setPhase('ending');
    clearTimeout(phaseTimer.current);
    phaseTimer.current = setTimeout(() => goNext(), 2000);
  }, [items.length, goNext]);

  // Fallback: if no trailer or video errors, auto-advance after 8s
  useEffect(() => {
    const item = items[current];
    if (!item?.trailer_url) {
      const fallback = setTimeout(() => {
        if (items.length > 1) goNext();
      }, 8000);
      return () => clearTimeout(fallback);
    }
  }, [current, items, goNext]);

  const goTo = (dir) => {
    clearTimeout(phaseTimer.current);
    setCurrent(prev => {
      if (dir === 'next') return (prev + 1) % items.length;
      return prev === 0 ? items.length - 1 : prev - 1;
    });
  };

  if (!items.length) return null;
  const item = items[current];
  const hasTrailer = !!item.trailer_url;
  const showVideo = hasTrailer && phase === 'trailer';

  return (
    <div className="pt-20 md:pt-[84px] px-6 md:px-20" data-testid="hero-banner-frame">
    <div className="relative w-full h-[68vh] md:h-[84vh] overflow-hidden rounded-2xl md:rounded-3xl shadow-[0_25px_70px_rgba(0,0,0,0.75)]" data-testid="hero-banner">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={item.banner_url || item.thumbnail_url}
          alt={item.title}
          className="w-full h-full object-cover transition-opacity duration-700"
          style={{ opacity: showVideo ? 0 : 1, zIndex: 2 }}
          loading="eager"
        />
        {hasTrailer && (
          <video
            ref={videoRef}
            key={`${item.id}-${current}`}
            src={item.trailer_url}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{ opacity: showVideo ? 1 : 0 }}
            muted={muted} autoPlay playsInline
            onLoadedMetadata={(e) => { const ts = parseFloat(item.trailer_trim_start) || 0; if (ts > 0) e.target.currentTime = ts; }}
            onTimeUpdate={(e) => { const te = parseFloat(item.trailer_trim_end) || 0; if (te > 0 && e.target.currentTime >= te) { e.target.pause(); handleTrailerEnded(); } }}
            onEnded={handleTrailerEnded}
            onError={() => { if (items.length > 1) setTimeout(goNext, 3000); }}
          />
        )}
      </div>

      {/* Gradients */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#11041F] via-[#11041F]/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#11041F]/80 via-transparent to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 pb-32 md:pb-40 px-8 md:px-20 z-10 animate-slideUp" key={item.id}>
        {/* Maya+ Original badge — only for originals category */}
        {(item.categories?.includes('originals') || item.category === 'originals') && (
          <div className="flex items-center gap-4 mb-3">
            <span className="text-lg md:text-xl font-bold text-[#E9641A] tracking-wider uppercase">Maya+</span>
            <span className="text-lg md:text-xl font-semibold text-white/60">Original</span>
          </div>
        )}

        {item.logo_url ? (
          <img src={item.logo_url} alt={item.title}
            className="max-h-28 md:max-h-40 lg:max-h-48 w-auto mb-4 drop-shadow-2xl transition-opacity duration-700"
            style={{ opacity: showVideo ? 1 : 0 }}
            data-testid="hero-logo" />
        ) : (
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-extrabold tracking-tighter text-white mb-4 max-w-4xl leading-[0.9]" data-testid="hero-title">
            {item.title}
          </h1>
        )}

        <div className="flex items-center gap-4 mb-5">
          <span className="text-lg font-semibold text-white/80">{item.year}</span>
          <span className="px-3 py-1 bg-white/20 rounded text-sm font-bold backdrop-blur-sm">{item.rating}</span>
          {item.duration && <span className="text-lg text-white/60">{item.duration}</span>}
        </div>

        <p className="text-base md:text-lg text-white/70 max-w-2xl mb-8 line-clamp-3 leading-relaxed">
          {item.description}
        </p>

        <div className="flex items-center gap-4 flex-wrap">
          <button
            data-testid="hero-play-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('launchMedia', { detail: item.id }))}
            className="flex items-center gap-3 bg-[#75D4D5] text-white font-bold px-8 md:px-12 py-3.5 md:py-4 rounded-md hover:bg-[#75D4D5]/80 transition-all duration-200 text-lg"
          >
            <Play size={26} fill="white" />
            Lecture
          </button>
          <button
            data-testid="hero-info-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('openContentModal', { detail: item.id }))}
            className="flex items-center gap-3 bg-white/15 text-white font-semibold px-8 md:px-12 py-3.5 md:py-4 rounded-md backdrop-blur-md hover:bg-white/25 transition-all duration-200 text-lg"
          >
            <Info size={26} />
            Plus d'infos
          </button>
        </div>
      </div>

      {/* Right side controls */}
      <div className="absolute bottom-32 md:bottom-40 right-8 md:right-20 z-20 flex items-center gap-4">
        <button
          data-testid="hero-mute-btn"
          onClick={() => setMuted(!muted)}
          className="w-12 h-12 rounded-full border-2 border-white/30 flex items-center justify-center text-white/60 hover:text-white hover:border-white transition-all bg-black/20 backdrop-blur-sm"
        >
          {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
        </button>
        <span className="px-4 py-1.5 bg-white/10 border-l-2 border-white/60 text-base text-white/70 backdrop-blur-sm font-medium">
          {item.age_rating || '16+'}
        </span>
      </div>

      {/* Navigation arrows */}
      {items.length > 1 && (
        <>
          <button data-testid="hero-prev-btn" onClick={() => goTo('prev')}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-24 bg-black/30 flex items-center justify-center text-white/50 hover:text-white hover:bg-black/50 transition-all rounded-r">
            <ChevronLeft size={32} />
          </button>
          <button data-testid="hero-next-btn" onClick={() => goTo('next')}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-24 bg-black/30 flex items-center justify-center text-white/50 hover:text-white hover:bg-black/50 transition-all rounded-l">
            <ChevronRight size={32} />
          </button>
        </>
      )}

      {/* Slide indicators */}
      {items.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {items.map((_, idx) => (
            <button key={idx} onClick={() => { clearTimeout(phaseTimer.current); setCurrent(idx); }}
              className={`h-1 rounded-full transition-all duration-500 ${idx === current ? 'w-8 bg-[#E9641A]' : 'w-3 bg-white/30 hover:bg-white/50'}`} />
          ))}
        </div>
      )}
    </div>
    </div>
  );
}
